import { useCallback, useEffect, useRef, useState } from 'react';

import {
    MAX_SIDEBAR_WIDTH,
    MIN_SIDEBAR_HEIGHT,
    MIN_SIDEBAR_WIDTH,
    ResizeEdge,
    SIDE_SPACING,
    TOP_SPACING
} from './agent-portal.styles';

interface Position {
    x: number;
    y: number;
}

interface UseAgentResizeProps {
    width: number;
    setWidth: (w: number) => void;
    height: number;
    setHeight: (h: number) => void;
    position: Position;
    isDocked: boolean;
    dockSide: 'left' | 'right';
    onUndock: (pos: Position) => void;
    onUpdatePosition: (pos: Position) => void;
    portalContainerRef: React.RefObject<HTMLDivElement | null>;
}

export const useAgentResize = ({
    width,
    setWidth,
    height,
    setHeight,
    position,
    isDocked,
    dockSide,
    onUndock,
    onUpdatePosition,
    portalContainerRef
}: UseAgentResizeProps) => {
    const [isResizingWidth, setIsResizingWidth] = useState(false);
    const [isResizingHeight, setIsResizingHeight] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [resizingEdge, setResizingEdge] = useState<ResizeEdge | null>(null);

    const resizingRef = useRef({
        startHeight: 0,
        startPosition: { x: 0, y: 0 },
        startWidth: 0,
        startX: 0,
        startY: 0
    });

    // Use refs for frequently-changing values to avoid effect re-runs
    const isDockedRef = useRef(isDocked);
    const widthRef = useRef(width);
    const heightRef = useRef(height);
    const dockSideRef = useRef(dockSide);
    const setWidthRef = useRef(setWidth);
    const setHeightRef = useRef(setHeight);
    const onUndockRef = useRef(onUndock);
    const onUpdatePositionRef = useRef(onUpdatePosition);

    useEffect(() => {
        isDockedRef.current = isDocked;
        widthRef.current = width;
        heightRef.current = height;
        dockSideRef.current = dockSide;
        setWidthRef.current = setWidth;
        setHeightRef.current = setHeight;
        onUndockRef.current = onUndock;
        onUpdatePositionRef.current = onUpdatePosition;
    });

    useEffect(() => {
        if (!isResizingWidth && !isResizingHeight && !isDragging && !resizingEdge) {
            return undefined;
        }

        const handleMouseMove = (e: MouseEvent) => {
            const { clientX, clientY } = e;
            const { startX, startY, startWidth, startHeight, startPosition } = resizingRef.current;
            const deltaX = clientX - startX;
            const deltaY = clientY - startY;

            // Handle dragging (both undocking and dragging floating sidebar)
            if (isDragging) {
                const currentIsDocked = isDockedRef.current;

                if (currentIsDocked) {
                    // Still docked - check if we should undock
                    const DRAG_THRESHOLD = 10;
                    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
                        // Calculate the sidebar's current position in viewport
                        const sidebarEl = portalContainerRef.current?.firstElementChild;
                        if (sidebarEl) {
                            const rect = sidebarEl.getBoundingClientRect();
                            // Ensure we have a valid height when undocking
                            const currentHeight = heightRef.current > 0 ? heightRef.current : rect.height;
                            setHeightRef.current(currentHeight);

                            // Undock at current position
                            const newX = rect.left;
                            const newY = rect.top;

                            onUndockRef.current({ x: newX, y: newY });

                            // Update ref so next mousemove calculates from this new position
                            // This prevents the jump by resetting the baseline
                            resizingRef.current.startPosition = { x: newX, y: newY };
                            resizingRef.current.startX = clientX;
                            resizingRef.current.startY = clientY;
                        }
                    }
                } else {
                    // Already floating - drag it with viewport constraints
                    let newX = startPosition.x + deltaX;
                    let newY = startPosition.y + deltaY;

                    // Constrain to viewport using existing spacing constants
                    const minX = SIDE_SPACING;
                    const minY = TOP_SPACING;
                    const maxX = window.innerWidth - widthRef.current - SIDE_SPACING;
                    const maxY = window.innerHeight - heightRef.current - SIDE_SPACING;

                    newX = Math.max(minX, Math.min(newX, maxX));
                    newY = Math.max(minY, Math.min(newY, maxY));

                    onUpdatePositionRef.current({ x: newX, y: newY });
                }
                return;
            }

            // Handle docked mode resizing
            if (isResizingWidth && isDockedRef.current) {
                const widthChange = dockSideRef.current === 'right' ? -deltaX : deltaX;
                const newWidth = Math.min(Math.max(startWidth + widthChange, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
                setWidthRef.current(newWidth);
            }

            if (isResizingHeight && isDockedRef.current) {
                const heightChange = -deltaY;
                const maxHeight = window.innerHeight - TOP_SPACING - SIDE_SPACING;
                const newHeight = Math.min(Math.max(startHeight + heightChange, MIN_SIDEBAR_HEIGHT), maxHeight);
                setHeightRef.current(newHeight);
            }

            // Handle floating mode resizing
            if (resizingEdge) {
                const edge = resizingEdge;
                let newWidth = startWidth;
                let newHeight = startHeight;
                let newX = startPosition.x;
                let newY = startPosition.y;

                // Calculate new dimensions and position based on edge
                if (edge.includes('left')) {
                    newWidth = Math.max(startWidth - deltaX, MIN_SIDEBAR_WIDTH);
                    newX = startPosition.x + (startWidth - newWidth);
                } else if (edge.includes('right')) {
                    newWidth = Math.max(startWidth + deltaX, MIN_SIDEBAR_WIDTH);
                }

                if (edge.includes('top')) {
                    newHeight = Math.max(startHeight - deltaY, MIN_SIDEBAR_HEIGHT);
                    newY = startPosition.y + (startHeight - newHeight);
                } else if (edge.includes('bottom')) {
                    newHeight = Math.max(startHeight + deltaY, MIN_SIDEBAR_HEIGHT);
                }

                // Constrain to viewport
                const maxWidth = Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth - newX - SIDE_SPACING);
                const maxHeight = window.innerHeight - newY - SIDE_SPACING;

                newWidth = Math.min(newWidth, maxWidth);
                newHeight = Math.min(newHeight, maxHeight);

                setWidthRef.current(newWidth);
                setHeightRef.current(newHeight);
                onUpdatePositionRef.current({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsResizingWidth(false);
            setIsResizingHeight(false);
            setIsDragging(false);
            setResizingEdge(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        // Set appropriate cursor
        if (isDragging) document.body.style.cursor = 'grabbing';
        else if (isResizingWidth) document.body.style.cursor = 'col-resize';
        else if (isResizingHeight) document.body.style.cursor = 'ns-resize';
        else if (resizingEdge) {
            const cursorMap: Record<ResizeEdge, string> = {
                bottom: 'ns-resize',
                'bottom-left': 'nesw-resize',
                'bottom-right': 'nwse-resize',
                left: 'ew-resize',
                right: 'ew-resize',
                top: 'ns-resize',
                'top-left': 'nwse-resize',
                'top-right': 'nesw-resize'
            };
            document.body.style.cursor = cursorMap[resizingEdge];
        }

        document.body.style.userSelect = 'none';

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        // Only re-run effect when resize/drag state changes (boolean flags)
        // All other values use refs to avoid triggering effect re-runs
    }, [isResizingWidth, isResizingHeight, isDragging, resizingEdge, portalContainerRef]);

    const handleResizeWidthStart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizingWidth(true);

        // Get current height to use as baseline if we're switching from auto mode
        // If height is 0, calculate the actual rendered height
        let currentStartHeight = height;
        if (currentStartHeight === 0 && portalContainerRef.current?.firstElementChild) {
            currentStartHeight = portalContainerRef.current.firstElementChild.getBoundingClientRect().height;
        }

        resizingRef.current = {
            startHeight: currentStartHeight,
            startPosition: { x: position.x, y: position.y },
            startWidth: width,
            startX: e.clientX,
            startY: e.clientY
        };
    };

    const handleResizeHeightStart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizingHeight(true);

        // If height is currently 0 (auto), we need to get the computed height to start resizing from
        let currentStartHeight = height;
        if (currentStartHeight === 0 && portalContainerRef.current?.firstElementChild) {
            currentStartHeight = portalContainerRef.current.firstElementChild.getBoundingClientRect().height;
            // We update state immediately so we snap to pixel value instead of "auto"
            setHeight(currentStartHeight);
        }

        resizingRef.current = {
            startHeight: currentStartHeight,
            startPosition: { x: position.x, y: position.y },
            startWidth: width,
            startX: e.clientX,
            startY: e.clientY
        };
    };

    const handleDragStart = useCallback(
        (e: React.MouseEvent | MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            setIsDragging(true);

            resizingRef.current = {
                startHeight: height,
                startPosition: { x: position.x, y: position.y },
                startWidth: width,
                startX: e.clientX,
                startY: e.clientY
            };
        },
        [height, position, width]
    );

    const handleFloatingResizeStart = (e: React.MouseEvent, edge: ResizeEdge) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingEdge(edge);

        resizingRef.current = {
            startHeight: height,
            startPosition: { x: position.x, y: position.y },
            startWidth: width,
            startX: e.clientX,
            startY: e.clientY
        };
    };

    return {
        handleDragStart,
        handleFloatingResizeStart,
        handleResizeHeightStart,
        handleResizeWidthStart,
        isDragging,
        isResizingHeight,
        isResizingWidth,
        resizingEdge
    };
};
